/**
 * Default quote coefficients — a base set and one per order quantity. The same editor for the company defaults (admin
 * page) and for "my defaults" (My settings, JF 2026-10-16: colleagues could not find where to set them). Next to each
 * set: the GP the cost sheet open now would have with it.
 */
import { useEffect, useState } from "react";
import { DEFAULT_QCOEF, NEUTRAL_QCOEF, hasSheet, n, totals, type CoefPrefs, type QCoef } from "@ic/engine";
import { api } from "../../shared/api.ts";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { Field } from "../../shared/ui/common.tsx";
import { COEF_FIELDS } from "../cost-sheet/coef.ts";

const show = (v: number, kind: "pct" | "x") => (kind === "pct" ? String(+(v * 100).toFixed(4)) : String(v));
const read = (v: string, kind: "pct" | "x") => (kind === "pct" ? n(v) / 100 : n(v));

export function CoefEditor({ scope }: { scope: "company" | "mine" }) {
  const t = useT();
  const toast = useStore.getState().toast;
  const sheet = useStore((s) => s.sheet);
  const [base, setBase] = useState<QCoef>({ ...DEFAULT_QCOEF });
  const [rows, setRows] = useState<{ qty: number; c: QCoef }[]>([]);
  const [newQty, setNewQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [company, setCompany] = useState<CoefPrefs | null>(null);
  useEffect(() => {
    api.coefPrefs().then((r) => {
      setCompany(r.company ?? null);
      const own = scope === "company" ? r.company : r.mine;
      setBase({ ...(scope === "mine" ? NEUTRAL_QCOEF : DEFAULT_QCOEF), ...(own?.base || {}) });
      setRows(Object.entries(own?.byQty || {}).map(([q, c]) => ({ qty: Number(q), c: { ...DEFAULT_QCOEF, ...c } })).sort((a, b) => a.qty - b.qty));
      setLoaded(true);
    }).catch((e) => toast(String(e.message || e)));
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  function addQty() {
    const q = Math.round(n(newQty));
    if (!(q > 0)) return;
    if (rows.some((r) => r.qty === q)) { toast(t(`${q} pcs is already in the list`, `${q} 件已经在列表里`)); return; }
    setRows([...rows, { qty: q, c: { ...base } }].sort((a, b) => a.qty - b.qty)); setNewQty("");
  }
  async function save() {
    setBusy(true);
    try {
      const p: CoefPrefs = { base, byQty: Object.fromEntries(rows.map((r) => [String(r.qty), r.c])) };
      const st = useStore.getState();
      if (scope === "company") { await api.saveCompanyCoef(p); st.setCoefPrefs({ mine: st.coefPrefs?.mine ?? null, company: p }); }
      else { await api.saveMyCoef(p); st.setCoefPrefs({ mine: p, company: st.coefPrefs?.company ?? null }); }
      toast(scope === "company" ? t("Company defaults saved", "公司默认系数已保存") : t("Your defaults saved — new cost sheets use them", "已保存你的默认系数——新建的成本表都会用"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  async function useCompany() {
    if (!confirm(t("Save the company coefficients as your defaults for new sheets?", "将公司系数保存为你新建成本表的默认值？"))) return;
    setBusy(true);
    try {
      const next: CoefPrefs = { ...company, base: { ...DEFAULT_QCOEF, ...company?.base } };
      await api.saveMyCoef(next);
      const st = useStore.getState(); st.setCoefPrefs({ mine: next, company: st.coefPrefs?.company ?? null });
      setBase(next.base!);
      setRows(Object.entries(next.byQty || {}).map(([q, c]) => ({ qty: Number(q), c: { ...c } })).sort((a, b) => a.qty - b.qty));
      toast(t("Company coefficients saved as your defaults", "已将公司系数保存为你的默认值"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  /** the GP the sheet open now would have with these coefficients (only the GP; the sheet does not change) */
  const gpWith = (c: QCoef) => {
    if (!hasSheet(sheet) || !sheet.qcoef) return "—";
    const tt = totals({ ...sheet, qcoef: c });
    return tt.qfob > 0 ? (tt.gpp * 100).toFixed(1) + "%" : "—"; // an empty sheet has no GP
  };
  const cells = (c: QCoef, on: (c: QCoef) => void, key: string) => COEF_FIELDS.map(([k, , , kind]) => (
    <td key={k}><Field className="in n" dataK={`${scope === "company" ? "cc" : "mc"}-${key}-${k}`} value={show(c[k] as number, kind)} onChange={(v) => on({ ...c, [k]: read(v, kind) })} /></td>
  ));
  if (!loaded) return <div className="dim">{t("Loading…", "加载中…")}</div>;
  const id = scope === "company" ? "cc" : "mc";
  return (
    <div id={scope === "company" ? "companyCoefEd" : "myCoef"}>
      <p className="dim" style={{ marginTop: 0, fontSize: 12.5 }}>{scope === "company" ? t(
        "Company coefficients apply to personal sheets only when the user selects Use company defaults. Existing saved sheets keep their coefficients. A quantity not listed uses the largest listed quantity below it (else the smallest).",
        "用户点击使用公司默认后，个人成本表才套用公司系数。已保存的成本表保持不变。没列出的数量，用不超过它的最大一档（都比它大就用最小一档）。")
        : t("Your defaults for NEW cost sheets; existing sheets keep theirs. Without saved personal defaults, all percentages start at 0 and CM factor at 1 (no markup). Company defaults apply only when explicitly selected.",
          "你新建成本表的默认系数；已有成本表不变。没有个人默认时，所有百分比从 0 开始，CM 倍数为 1（不加价）。公司默认需要主动选择才会使用。")}</p>
      <div className="coefwrap"><table className="t coefgrid" id={id + "Table"}><tbody>
        <tr><th>{t("Order qty", "数量档")}</th>{COEF_FIELDS.map(([k, en, zh, kind]) => <th key={k}>{t(en, zh)}{kind === "pct" ? " %" : ""}</th>)}
          <th title={t("The GP the cost sheet open now would have with this set", "当前打开的成本表用这组系数时的 GP")}>{t("GP on the open sheet", "当前表 GP")}</th><th /></tr>
        <tr data-coefrow="base"><td><b>{t("Base (any qty)", "基础（不分数量）")}</b></td>{cells(base, setBase, "base")}<td className="mono" data-gp="base">{gpWith(base)}</td><td /></tr>
        {rows.map((r, i) => (
          <tr key={r.qty} data-coefrow={r.qty}><td className="mono">{r.qty} {t("pcs", "件")}</td>
            {cells(r.c, (c) => setRows(rows.map((x, j) => (j === i ? { ...x, c } : x))), String(r.qty))}
            <td className="mono" data-gp={r.qty}>{gpWith(r.c)}</td>
            <td><button className="btn sm" data-coefdel={r.qty} onClick={() => setRows(rows.filter((_, j) => j !== i))}>{t("Remove", "删除")}</button></td></tr>
        ))}
      </tbody></table></div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input className="in" id={id + "NewQty"} style={{ maxWidth: 140 }} value={newQty} onChange={(e) => setNewQty(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addQty(); }} placeholder={t("e.g. 300", "如 300")} />
        <button className="btn sm" id={id + "Add"} onClick={addQty}>{t("+ Add quantity", "+ 加数量档")}</button>
        {scope === "company" ? <button className="btn sm" onClick={() => setBase({ ...DEFAULT_QCOEF })}>{t("Base = template values", "基础恢复模板值")}</button>
          : <button className="btn sm" id="mcClear" disabled={busy} onClick={useCompany}>{t("Use company defaults", "使用公司默认")}</button>}
        <button className="btn sm p" id={id + "Save"} style={{ marginLeft: "auto" }} disabled={busy} onClick={save}>{busy ? t("Saving…", "保存中…") : t("Save", "保存")}</button>
      </div>
    </div>
  );
}
